import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Check, ArrowRight } from "lucide-react";
import { PaymentBadges } from "@/components/PaymentBadges";
import { BackHome } from "@/components/BackHome";
import { trackCompleteRegistration } from "@/analytics/metaPixel";

const eur = (cents) => `€${(cents / 100).toFixed(0)}`;
const installment = (cents) => `€${(cents / 300).toFixed(2).replace(".", ",")}`;

export default function Register() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);

  const packKey = searchParams.get("pack");
  const selected = packages.find((p) => p.lookup_key === packKey);

  useEffect(() => {
    api.get("/payments/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to={user.role === "coach" ? "/coach" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, privacy_accepted: privacy, referral_code: searchParams.get("ref") || null });
      trackCompleteRegistration();
      setUser(data);
      navigate(packKey ? `/app/packages?buy=${packKey}` : "/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative">
      <BackHome />
      <img
        src="https://images.unsplash.com/photo-1641337221253-fdc7237f6b61?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="" className="absolute inset-0 w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/40" />
      <div className="relative w-full max-w-md" data-testid="register-wrapper">
        {selected && (
          <div className="kc-card p-6 mb-4 border-primary/50" data-testid="register-pack-summary">
            <p className="kc-eyebrow mb-2">{t("reg.packSummary")}</p>
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-black text-2xl uppercase tracking-tight">{selected.name}</h2>
              <p className="font-mono text-3xl font-black" data-testid="register-pack-price">{eur(selected.amount)}</p>
            </div>
            <p className="text-sm text-muted-foreground">{selected.days} {t("reg.daysAccess")}</p>
            <p className="text-sm font-semibold text-primary mt-2" data-testid="register-pack-installments">
              {t("reg.installmentsA")} {installment(selected.amount)} {t("reg.installmentsB")}
            </p>
            <ul className="space-y-1.5 mt-4">
              {(selected.features || []).slice(0, 4).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="register-pack-switcher">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground self-center">{t("reg.packChange")}:</span>
              {packages.filter((p) => p.lookup_key !== packKey).map((p) => (
                <button key={p.lookup_key} type="button" data-testid={`register-switch-${p.lookup_key}`}
                  onClick={() => setSearchParams({ pack: p.lookup_key })}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:text-primary transition-colors">
                  {p.name} · {eur(p.amount)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="kc-card p-8" data-testid="register-card">
          {!selected && (
            <p className="text-sm font-semibold text-accent mb-4 kc-badge bg-accent/12 !normal-case" data-testid="register-trial-badge">
              {t("reg.trialBadge")}
            </p>
          )}
          <div className="flex justify-center mb-6">
            <img src="/adtraining-full-t.png" alt="AD TRAINING PROGRAM" className="h-36 w-auto kc-logo-ondark" />
            <img src="/adtraining-full-onlight.png" alt="AD TRAINING PROGRAM" className="h-36 w-auto kc-logo-onlight" />
          </div>
          <h1 className="text-3xl font-black uppercase tracking-tight mb-1">{t("reg.title")}</h1>
          <p className="text-muted-foreground text-sm mb-6">{t("reg.subtitle")}</p>
          {searchParams.get("ref") && (
            <p className="text-sm text-primary font-semibold mb-4 kc-badge bg-primary/12 !normal-case" data-testid="register-referral-badge">
              {t("ref.badge")}
            </p>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("reg.name")}</label>
              <input data-testid="register-name-input" type="text" required value={name}
                onChange={(e) => setName(e.target.value)} className="kc-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("login.email")}</label>
              <input data-testid="register-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="kc-input mt-1" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("login.password")}</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)} className="kc-input mt-1" />
            </div>
            <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer" data-testid="register-privacy-label">
              <input data-testid="register-privacy-checkbox" type="checkbox" required checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)} className="mt-1 accent-[hsl(var(--primary))]" />
              <span>
                {t("reg.privacy")}{" "}
                <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link>
              </span>
            </label>
            {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
            <button data-testid="register-submit-button" type="submit" disabled={loading || !privacy} className="kc-btn-primary w-full">
              {loading ? t("common.loading") : t("reg.submit")}
            </button>
          </form>
          <div className="mt-5 flex flex-col gap-2 text-sm">
            <Link data-testid="register-login-link" to="/login" className="text-muted-foreground hover:text-foreground">
              {t("reg.haveAccount")}
            </Link>
          </div>
        </div>
        {!selected && packages.length > 0 && (
          <div className="kc-card p-6 mt-4" data-testid="register-pack-strip">
            <p className="kc-eyebrow mb-4">{t("reg.choosePack")}</p>
            <div className="space-y-2">
              {packages.map((p) => (
                <button key={p.lookup_key} type="button" data-testid={`register-pick-${p.lookup_key}`}
                  onClick={() => setSearchParams({ pack: p.lookup_key })}
                  className="w-full px-4 py-3 rounded-xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left">
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="font-bold text-sm uppercase tracking-tight whitespace-nowrap">{p.name}</span>
                    <span className="font-mono font-black shrink-0">{eur(p.amount)}</span>
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5 whitespace-nowrap">{p.days} {t("reg.daysAccess")}</span>
                  <span className="block text-xs text-primary font-semibold mt-1">
                    {t("reg.installmentsA")} {installment(p.amount)} {t("reg.installmentsB")}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4" data-testid="register-onetime-note">{t("reg.oneTime")}</p>
          </div>
        )}
        {selected && (
          <p className="text-xs text-muted-foreground mt-3 px-2" data-testid="register-onetime-note">{t("reg.oneTime")}</p>
        )}
        <div className="mt-4 px-2"><PaymentBadges compact /></div>
      </div>
    </div>
  );
}
